import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from "@mui/x-data-grid";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import moment from "moment";
import { useSelector } from "react-redux";
import TimePeriods from "../table/panel/TimePeriods";
import ServicingApi from "../../services/AssociatedServices";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import ConfirmDialog from "../cofirm/ConfirmDialog";
import translations from "../../assets/locals/translations";
import PersistentDataGrid from "../table/PersistentDataGrid";

const TractorSoldOut = () => {
  const ServiceApi = new ServicingApi();
  const [stockData, setStockData] = useState([]);
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedType, setSelectedType] = useState("Today");

  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Page state (0-indexed for DataGrid)
  const [page, setPage] = useState(0);
  const pageSizeKey = `rowsPerPage_${window.location.pathname}`;
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem(pageSizeKey);
    return saved ? parseInt(saved, 10) : 50;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [excelResult, setExcelResult] = useState([]); // Kept for reference, but handleExportAll fetches fresh data
  const [open, setOpen] = useState({ visible: false, rowData: null });

  const getAllDates = (dates) => {
    if (!dates || dates.length === 0) return [];
    const [startDate, endDate] = dates.map((date) => new Date(date));
    const dateArr = [];
    const formatDate = (date) => {
      let year = date.getFullYear();
      let month = (date.getMonth() + 1).toString().padStart(2, "0");
      let day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    let currentDate = new Date(startDate);
    if (endDate === undefined) {
      dateArr.push(formatDate(currentDate));
      return dateArr;
    } else {
      while (currentDate <= endDate) {
        dateArr.push(formatDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dateArr;
    }
  };

  const getSoldTractorLogs = async (
    page = 0,
    pageSize = 50,
    dates,
    type,
    search = searchQuery
  ) => {
    // Basic validation
    if (!type) return;

    let startDate = "";
    let endDate = "";

    // Handle date logic
    if (type === "All") {
      startDate = "";
      endDate = "";
    } else if (type === "Custom" && dates.length > 0) {
      const customDates = getAllDates(dates);
      startDate = customDates[0];
      endDate = customDates[customDates.length - 1];
    } else if (dates.length > 0) {
      startDate = dates[0];
      endDate = dates[dates.length - 1];
    }

    setLoading(true);
    try {
      const payload = {
        ev_stage: "SOLD",
        category: 2,
        page: page + 1, // API expects 1-based index
        limit: pageSize,
        searchQuery: search,
        ...(search.trim() === "" && { startDate, endDate }),
      };

      const response = await ServiceApi.getSoldTractorLogs(payload);
      if (response && response.isSuccess) {
        const result = response.result;
        setStockData(result);
        setTotalCount(response.totalCount);
        setExcelResult(response.excelResult);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when page or pageSize changes
  useEffect(() => {
    if (selectedDates.length > 0 || selectedType === "All") {
      getSoldTractorLogs(
        page,
        pageSize,
        selectedDates,
        selectedType,
        searchQuery
      );
    }
  }, [page, pageSize]);

  // Fetch data when searchQuery changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(0);
      getSoldTractorLogs(
        0,
        pageSize,
        selectedDates,
        selectedType,
        searchQuery
      );
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const columns = [
    {
      field: "customer_name",
      headerName: t.customerName || "Customer Name",
      flex: 1,
      minWidth: 310,
    },
    {
      field: "village",
      headerName: t.village || "Village",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "taluka",
      headerName: t.taluka || "Taluka",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "phone_number",
      headerName: t.phoneNumber || "Phone Number",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "delivery_date",
      headerName: t.deliveryDate || "Delivery Date",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    {
      field: "sold_date",
      headerName: t.soldDate || "Sold Date",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    { field: "sold_notes", headerName: t.soldNote || "Sold Note", flex: 1, minWidth: 150 },
    { field: "new_modal", headerName: t.model || "Model", flex: 1, minWidth: 150 },
    {
      field: "chassis_number",
      headerName: t.chassisNo || "CHASSIS NO",
      flex: 1,
      minWidth: 200,
    },
    { field: "engine_number", headerName: t.engineNo || "ENGINE NO", flex: 1, minWidth: 200 },
  ];

  const excelColumns = [
    { field: "customer_name", headerName: "Customer Name" },
    { field: "village", headerName: "Village" },
    { field: "taluka", headerName: "Taluka" },
    { field: "phone_number", headerName: "Phone Number" },
    { field: "delivery_date", headerName: "Delivery Date" },
    { field: "sold_date", headerName: "Sold Date" },
    { field: "sold_notes", headerName: "Sold Notes" },
    { field: "manufacturer_name", headerName: "Manufacture" },
    { field: "new_modal", headerName: "Model" },
    { field: "chassis_number", headerName: "Chassis No" },
    { field: "engine_number", headerName: "Engine No" },
  ];

  // ===============================================
  // EXPORT ALL LOGIC
  // ===============================================

  const generateCSV = (columns, data) => {
    const headers = columns.map((col) => col.headerName);
    const rows = data.map((row) =>
      columns.map((col) => {
        let raw = row[col.field];
        // Format dates
        if (
          raw != null &&
          (col.field === "date" ||
            col.field === "delivery_date" ||
            col.field === "enquiry_date" ||
            col.field === "retail_date" ||
            col.field === "sold_date")
        ) {
          const m = moment(raw);
          raw = m.isValid() ? m.format("DD-MM-YYYY") : "";
        }
        // Escape strings
        if (raw != null) {
          return String(raw).replace(/"/g, '""');
        }
        return "";
      })
    );
    const csvLines = [
      headers.join(","),
      ...rows.map((cols) => `"${cols.join('","')}"`),
    ];
    return csvLines.join("\n");
  };

  const handleExportAll = async () => {
    try {
      setIsExporting(true);

      // Determine dates
      let startDateStr = "";
      let endDateStr = "";

      if (selectedType === "All") {
        startDateStr = "";
        endDateStr = "";
      } else if (selectedDates.length > 0) {
        // If Custom, get the range from helper, else use selectedDates directly
        if (selectedType === "Custom") {
          const customDates = getAllDates(selectedDates);
          startDateStr = customDates[0];
          endDateStr = customDates[customDates.length - 1];
        } else {
          startDateStr = selectedDates[0];
          endDateStr = selectedDates[selectedDates.length - 1];
        }
      }

      // API Payload: Request ALL records
      const payload = {
        ev_stage: "SOLD",
        category: 2,
        page: 1,
        limit: totalCount > 0 ? totalCount : 5000,
        searchQuery: searchQuery,
        ...(searchQuery.trim() === "" && { startDate: startDateStr, endDate: endDateStr }),
      };

      const response = await ServiceApi.getSoldTractorLogs(payload);

      if (response && response.isSuccess) {
        // Use excelResult if available (usually contains enriched data), otherwise result
        const dataToExport = response.excelResult && response.excelResult.length > 0
          ? response.excelResult
          : response.result;

        if (dataToExport.length === 0) {
          alert("No data to export");
          return;
        }

        const csvContent = generateCSV(excelColumns, dataToExport);

        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const fileName = `SOLD_OUT_ALL_${yyyy}-${mm}-${dd}.csv`;

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("Failed to export data.");
      }
    } catch (error) {
      console.error("Export Error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // ===============================================
  // CUSTOM TOOLBAR COMPONENT
  // ===============================================

  const CustomToolbar = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
      setAnchorEl(null);
    };

    const onDownloadCSV = () => {
      handleExportAll();
      handleCloseMenu();
    }

    const onPrint = () => {
      window.print();
      handleCloseMenu();
    }

    return (
      <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />

        {/* Custom Export Button with Menu */}
        <Button
          id="export-button"
          aria-controls={openMenu ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? 'true' : undefined}
          onClick={handleClick}
          startIcon={<FileDownloadIcon />}
          disabled={loading || isExporting || totalCount === 0}
          sx={{
            color: 'primary.main',
            textTransform: 'uppercase',
            fontSize: '0.8125rem',
            padding: '4px 5px'
          }}
        >
          {isExporting ? "EXPORTING..." : "EXPORT"}
        </Button>
        <Menu
          id="export-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          MenuListProps={{
            'aria-labelledby': 'export-button',
          }}
        >
          <MenuItem onClick={onDownloadCSV}>Download as CSV</MenuItem>
          <MenuItem onClick={onPrint}>Print</MenuItem>
        </Menu>

      </GridToolbarContainer>
    );
  };

  const handleApplyButton = async (dates, type) => {
    setSelectedType(type);
    setSelectedDates(dates);
    setPage(0);
    await getSoldTractorLogs(0, pageSize, dates, type, searchQuery);
  };

  const undoSoldOutStatus = async (rowData) => {
    if (!rowData) {
      return;
    }
    setLoading(true);
    const payload = {
      customer_id: rowData.id,
      enquiry_id: rowData.enquiry_id,
      is_revert: true,
    };
    try {
      const response = await ServiceApi.undoSoldOutStatus(payload);
      if (response && response.isSuccess) {
        await getSoldTractorLogs(
          page,
          pageSize,
          selectedDates,
          selectedType,
          searchQuery
        );
        dispatch(setShowMessage(response.message));
        handleClose();
      }
    } catch (error) {
      console.error(error, "Error undoSoldOutStatus");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (rowData) => setOpen({ visible: true, rowData: rowData });
  const handleClose = () => setOpen({ visible: false, rowData: null });

  const handleRevert = async () => {
    try {
      await undoSoldOutStatus(open.rowData);
    } catch (error) {
      console.log(error, "error in revert");
    }
  };

  return (
    <div className="bg-white p-2">
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300 }}
        />
        <div className="d-flex justify-content-end ms-1">
          <TimePeriods handleApplyButton={handleApplyButton} />
          {/* Removed external download button since it's now in the toolbar */}
        </div>
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
      >
        <PersistentDataGrid
          rows={stockData}
          columns={[
            {
              field: "Menu",
              headerName: "Revert",
              flex: 1,
              minWidth: 50,
              renderCell: (params) => (
                <Tooltip title={t.revert || "Revert"}>
                  <button
                    onClick={() => {
                      handleOpen(params.row);
                    }}
                    className="d-flex align-items-center bg-secondary p-1 rounded"
                    style={{ cursor: "pointer" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-arrow-return-right text-white"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M1.5 1.5A.5.5 0 0 0 1 2v4.8a2.5 2.5 0 0 0 2.5 2.5h9.793l-3.347 3.346a.5.5 0 0 0 .708.708l4.2-4.2a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 8.3H3.5A1.5 1.5 0 0 1 2 6.8V2a.5.5 0 0 0-.5-.5"
                      />
                    </svg>
                  </button>
                </Tooltip>
              ),
            },
            {
              field: "s_no",
              headerName: t.no || "No",
              width: 80,
              sortable: false,
              filterable: false,
              renderCell: (params) => {
                return (
                  page * pageSize +
                  params.api.getRowIndexRelativeToVisibleRows(params.row.id) +
                  1
                );
              },
            },
            ...columns,
          ]}
          getRowId={(row) => row.id}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(newModel) => {
            setPage(newModel.page);
            setPageSize(newModel.pageSize);
            try { sessionStorage.setItem(pageSizeKey, String(newModel.pageSize)); } catch (e) { }
          }}
          pageSizeOptions={[10, 15, 25, 50]}
          disableRowSelectionOnClick
          autoHeight={isMobile}

          // --- Use Custom Toolbar ---
          components={{
            Toolbar: CustomToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>{t.noDataAvailable || "No Data Available"}</span>
              </div>
            ),
          }}
          // ---------------------------

          componentsProps={{
            toolbar: {
              position: "right",
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{
            width: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              textTransform: "uppercase",
            },
          }}
          loading={loading}
        />
      </div>

      <ConfirmDialog
        open={open.visible}
        title={t.confirmRevert || "Confirm Revert"}
        content={t.areYouSureRevert || "Are you sure you want to revert this tractor from 'Sold Out' status?"}
        onConfirm={handleRevert}
        onCancel={handleClose}
        confirmText={t.yesRevert || "Yes, Revert"}
        cancelText={t.no || "No"}
      />
    </div>
  );
};

export default TractorSoldOut;